/**
 * chunkReload — self-heal for stale lazy-loaded chunks after a deploy.
 *
 * Vite emits content-hashed chunks (`AppShell-DcR0vRGO.js`). A tab that was
 * open across a deploy is still running the *previous* build's code, which has
 * the *previous* build's filenames baked in. When it lazy-loads a route, it
 * asks for a hash that no longer exists on the server — and because our SPA
 * catch-all (`/* /index.html 200`) answers every unmatched path with HTML,
 * the module loader gets `text/html` where it expected JS and throws
 * "Failed to fetch dynamically imported module".
 *
 * Vite fires a cancelable `vite:preloadError` on `window` for exactly this.
 * Recovery escalates, because one reload is not always enough here:
 *
 *   attempt 1 — plain reload. Wins when the new service worker has already
 *               activated, so the precache holds the current index.html.
 *   attempt 2 — unregister the service worker + empty Cache Storage, then
 *               reload. Our SW answers *every* navigation from precache
 *               (`NavigationRoute(createHandlerBoundToURL('index.html'))`),
 *               so while it still holds the old build a plain reload keeps
 *               returning the same dead shell — verified: with a stale
 *               precache even `fetch('/index.html', {cache:'reload'})` never
 *               reaches the network. Dropping the SW forces the next
 *               navigation to the network and ends the loop regardless of
 *               who wins the SW update race.
 *   attempt 3 — stop. The cause isn't a stale chunk (bad deploy, offline,
 *               blocked asset); let it through to ErrorBoundary so it's
 *               visible rather than trapping the user in a refresh loop.
 *
 * Clearing Cache Storage is safe: it holds only the Workbox precache and, in
 * local dev, cached app modules. Offline *data* and queued mutations live in
 * IndexedDB via RxDB/Dexie (src/offline/db.ts) and are untouched.
 *
 * Build-only by design — `vite:preloadError` comes from the `__vitePreload`
 * helper that Vite injects at build time. Dev serves native dynamic imports
 * and has no stale-hash problem, so there is nothing to handle there.
 */

const ATTEMPT_KEY = 'granula:chunk-reload'
const TOTAL_KEY = 'granula:chunk-reload-total'
const MAX_ATTEMPTS = 2
/**
 * Hard lifetime cap on automatic reloads per tab session. The per-episode
 * counter above is re-armed after a clean boot (so a long-lived tab survives
 * several deploys), and on a genuinely broken deploy that re-arm would
 * otherwise restart the escalation forever — a slow infinite reload loop,
 * which is worse than showing the error. This ceiling is never reset.
 */
const MAX_TOTAL = 6
/** Re-arm this long after a clean boot, so a long-lived tab survives many deploys. */
const REARM_AFTER_MS = 10_000
const DEV_SW_CLEANUP_KEY = 'granula:dev-sw-cleanup'

let registered = false
let sawErrorThisPage = false

/**
 * Remove the old local dev PWA once after dev caching is disabled.
 *
 * Vite's dev service worker caches native source modules, including React's
 * prebundled modules. If the dependency optimizer changes, a stale cache can
 * combine modules from two generations and make ordinary hooks fail with
 * "Cannot read properties of null". This only touches the dev worker and its
 * app-module caches; RxDB/Dexie data remains in IndexedDB.
 *
 * Returns true when the caller should reload after cleanup.
 */
export async function cleanupDevServiceWorker(): Promise<boolean> {
  if (!import.meta.env.DEV || import.meta.env.VITE_ENABLE_DEV_PWA === 'true') {
    return false
  }

  try {
    const registrations = 'serviceWorker' in navigator
      ? await navigator.serviceWorker.getRegistrations()
      : []
    const devRegistrations = registrations.filter((registration) =>
      [
        registration.active?.scriptURL,
        registration.installing?.scriptURL,
        registration.waiting?.scriptURL,
      ].some((scriptURL) => scriptURL?.includes('/dev-sw.js')),
    )

    const cacheNames = 'caches' in window ? await caches.keys() : []
    const devCacheNames = cacheNames.filter((name) =>
      name === 'granula-dev-assets' ||
      name.startsWith(`workbox-precache-v2-${window.location.origin}/`),
    )

    if (devRegistrations.length === 0 && devCacheNames.length === 0) {
      sessionStorage.removeItem(DEV_SW_CLEANUP_KEY)
      return false
    }

    // Avoid an infinite reload loop if the browser refuses cleanup.
    if (sessionStorage.getItem(DEV_SW_CLEANUP_KEY) === '1') return false
    sessionStorage.setItem(DEV_SW_CLEANUP_KEY, '1')

    await Promise.all(devRegistrations.map((registration) => registration.unregister()))
    await Promise.all(devCacheNames.map((name) => caches.delete(name)))
    return true
  } catch {
    sessionStorage.removeItem(DEV_SW_CLEANUP_KEY)
    return false
  }
}

function counter(key: string): number {
  const n = Number(sessionStorage.getItem(key))
  return Number.isFinite(n) && n > 0 ? n : 0
}

/**
 * Drop the service worker and every Cache Storage entry so the next navigation
 * must hit the network. Best-effort: a failure here must still fall through to
 * the reload, which is why everything is wrapped.
 */
async function purgeAppShell(): Promise<void> {
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations()
      await Promise.all(regs.map((r) => r.unregister()))
    }
    if ('caches' in window) {
      const keys = await caches.keys()
      await Promise.all(keys.map((k) => caches.delete(k)))
    }
  } catch {
    // ignore — reload anyway
  }
}

/** Register the global stale-chunk handler once at app boot (called from main.tsx). */
export function registerChunkErrorReload() {
  if (registered) return // idempotent
  registered = true

  // A page that ran this long without a chunk error has recovered; give a
  // long-lived tab a full budget again for the *next* deploy.
  setTimeout(() => {
    if (!sawErrorThisPage) sessionStorage.removeItem(ATTEMPT_KEY)
  }, REARM_AFTER_MS)

  window.addEventListener('vite:preloadError', (event) => {
    sawErrorThisPage = true
    const attempts = counter(ATTEMPT_KEY)
    const total = counter(TOTAL_KEY)

    if (attempts >= MAX_ATTEMPTS || total >= MAX_TOTAL) {
      console.error(
        `[chunkReload] giving up (attempt ${attempts}/${MAX_ATTEMPTS}, total ${total}/${MAX_TOTAL})`,
        event.payload,
      )
      return // no preventDefault → ErrorBoundary shows the error
    }

    // Write the counters *before* reload() — navigation can begin immediately
    // and a write scheduled after it may never land, re-arming the loop.
    sessionStorage.setItem(ATTEMPT_KEY, String(attempts + 1))
    sessionStorage.setItem(TOTAL_KEY, String(total + 1))

    // Must be synchronous: Vite reads defaultPrevented as soon as dispatch
    // returns, so this cannot wait on the async purge below.
    event.preventDefault()

    if (attempts === 0) {
      window.location.reload()
    } else {
      void purgeAppShell().then(() => window.location.reload())
    }
  })
}
